import { ReactNode, useReducer, useRef, useEffect, useCallback } from 'react';
import { cn } from '@utils/cn';
import styles from '@/styles/components/Dropdown.module.scss';

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
}

type DropdownAction = { type: 'TOGGLE' } | { type: 'CLOSE' };

const dropdownReducer = (state: boolean, action: DropdownAction): boolean => {
  switch (action.type) {
    case 'TOGGLE':
      return !state;
    case 'CLOSE':
      return false;
    default:
      return state;
  }
};

export const Dropdown = ({ trigger, children, align = 'right' }: DropdownProps) => {
  const [isOpen, dispatch] = useReducer(dropdownReducer, false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      dispatch({ type: 'CLOSE' });
    }
  }, []);

  const toggleDropdown = useCallback(() => dispatch({ type: 'TOGGLE' }), []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen, handleClickOutside]);

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <div onClick={toggleDropdown} className={styles.trigger}>
        {trigger}
      </div>
      {isOpen && (
        <div
          className={cn(
            styles.menu,
            align === 'right' ? styles.alignRight : styles.alignLeft
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};

interface DropdownItemProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  icon?: ReactNode;
}

export const DropdownItem = ({ children, onClick, className, icon }: DropdownItemProps) => {
  return (
    <button onClick={onClick} className={cn(styles.item, className)}>
      {icon && <span className={styles.icon}>{icon}</span>}
      {children}
    </button>
  );
};

export const DropdownDivider = () => {
  return <div className={styles.divider} />;
};
